import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, Mail, User, ShieldCheck, Clock, LogIn } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { useAuth, formatApiErrorDetail } from "../lib/auth";

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | pending
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Sesión iniciada");
        navigate("/", { replace: true });
      } else {
        await register(name, email, password);
        setMode("pending");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FFF7F0] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[920px] grid md:grid-cols-[1fr_420px] rounded-3xl overflow-hidden border border-[#F0E2D2] bg-white shadow-xl shadow-[#F26F21]/5">
        {/* Panel de marca */}
        <div className="hidden md:flex flex-col justify-between bg-[#241E18] p-10">
          <Logo dark />
          <div>
            <h1 className="font-display text-[34px] font-extrabold text-white leading-tight mb-3">
              Acceso <span className="text-[#FFB81C]">restringido</span>
            </h1>
            <p className="text-white/60 text-[14px] leading-relaxed">
              Natur Blur es una plataforma privada de anonimización de vídeo con Modo Forense.
              Solo los usuarios aprobados por el administrador pueden entrar.
            </p>
          </div>
          <div className="flex items-center gap-2.5 text-white/45 text-[12.5px]">
            <ShieldCheck size={15} className="text-[#F26F21]" />
            Tus vídeos se eliminan a las 24 horas · Inmutabilidad temporal garantizada
          </div>
        </div>

        {/* Formulario */}
        <div className="p-8 sm:p-10">
          {mode === "pending" ? (
            <div data-testid="register-pending-panel" className="h-full flex flex-col items-center justify-center text-center py-10">
              <span className="w-16 h-16 rounded-full bg-[#FEF6E7] border border-[#F5DFAE] flex items-center justify-center mb-5">
                <Clock size={26} className="text-[#92600A]" />
              </span>
              <h2 className="font-display text-[22px] font-extrabold text-[#241E18] mb-2">Solicitud enviada</h2>
              <p className="text-[13.5px] text-[#8A7F73] leading-relaxed mb-6">
                Tu cuenta está <b>pendiente de aprobación</b> por el administrador.
                Recibirás un email en <b>{email}</b> en cuanto esté lista.
              </p>
              <button
                data-testid="pending-back-to-login-btn"
                onClick={() => { setMode("login"); setError(""); }}
                className="h-11 px-6 rounded-full border-2 border-[#241E18] text-[#241E18] text-[13.5px] font-extrabold hover:bg-[#241E18] hover:text-white transition-all"
              >
                Volver al inicio de sesión
              </button>
            </div>
          ) : (
            <>
              <div className="md:hidden mb-6"><Logo /></div>
              <div className="grid grid-cols-2 gap-1.5 p-1 rounded-full bg-[#FFF3E9] border border-[#F5E7D8] mb-7">
                {[{ id: "login", label: "Entrar" }, { id: "register", label: "Solicitar acceso" }].map((t) => (
                  <button
                    key={t.id}
                    data-testid={`auth-tab-${t.id}`}
                    onClick={() => { setMode(t.id); setError(""); }}
                    className={`h-9 rounded-full text-[13px] font-extrabold transition-all ${
                      mode === t.id ? "bg-[#F26F21] text-white shadow-sm" : "text-[#8A7F73] hover:text-[#C2570F]"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <h2 className="font-display text-[24px] font-extrabold text-[#241E18] mb-1.5">
                {mode === "login" ? "Inicia sesión" : "Solicita tu cuenta"}
              </h2>
              <p className="text-[13px] text-[#8A7F73] mb-6">
                {mode === "login"
                  ? "Introduce tus credenciales aprobadas."
                  : "El administrador revisará tu solicitud y te avisará por email."}
              </p>

              <form onSubmit={submit} className="space-y-3.5">
                {mode === "register" && (
                  <label className="block">
                    <span className="text-[11.5px] font-extrabold uppercase tracking-wider text-[#8A7F73]">Nombre</span>
                    <span className="mt-1 flex items-center gap-2 h-12 rounded-xl border border-[#F0E2D2] bg-[#FFFBF7] px-3.5 focus-within:border-[#F26F21] transition-colors">
                      <User size={15} className="text-[#B4A896]" />
                      <input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)}
                        placeholder="Tu nombre" className="flex-1 bg-transparent outline-none text-[14px] font-semibold text-[#241E18]" />
                    </span>
                  </label>
                )}
                <label className="block">
                  <span className="text-[11.5px] font-extrabold uppercase tracking-wider text-[#8A7F73]">Email</span>
                  <span className="mt-1 flex items-center gap-2 h-12 rounded-xl border border-[#F0E2D2] bg-[#FFFBF7] px-3.5 focus-within:border-[#F26F21] transition-colors">
                    <Mail size={15} className="text-[#B4A896]" />
                    <input data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com" className="flex-1 bg-transparent outline-none text-[14px] font-semibold text-[#241E18]" />
                  </span>
                </label>
                <label className="block">
                  <span className="text-[11.5px] font-extrabold uppercase tracking-wider text-[#8A7F73]">Contraseña</span>
                  <span className="mt-1 flex items-center gap-2 h-12 rounded-xl border border-[#F0E2D2] bg-[#FFFBF7] px-3.5 focus-within:border-[#F26F21] transition-colors">
                    <Lock size={15} className="text-[#B4A896]" />
                    <input data-testid="auth-password-input" type="password" required minLength={8} value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={mode === "register" ? "Mínimo 8 caracteres" : "••••••••"}
                      className="flex-1 bg-transparent outline-none text-[14px] font-semibold text-[#241E18]" />
                  </span>
                </label>

                {error && (
                  <p data-testid="auth-error" className="rounded-xl bg-[#FDECEA] border border-[#F5C6C0] px-3.5 py-2.5 text-[12.5px] font-bold text-[#A4271C]">
                    {error}
                  </p>
                )}

                <button
                  data-testid="auth-submit-btn"
                  type="submit"
                  disabled={busy}
                  className="w-full h-12 rounded-full bg-[#F26F21] text-white text-[14.5px] font-extrabold hover:bg-[#D95B12] hover:shadow-lg hover:shadow-[#F26F21]/25 disabled:opacity-50 transition-all inline-flex items-center justify-center gap-2"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
                  {mode === "login" ? "Entrar" : "Enviar solicitud"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
